import { defaultMemoize } from 'reselect'
import BookingOffIcon from '../assets/integrations/booking-off.png'
import BookingOnIcon from '../assets/integrations/booking-on.png'
import AirnbnbOn from '../assets/integrations/airbnb-on.png'
import AirnbnbOff from '../assets/integrations/airbnb-off.png'
import CalendarImportIcon from '../assets/integrations/icalendar-arrow-left.png'
import CalendarExportIcon from '../assets/integrations/icalendar-arrow-right.png'
import CalendarOffIcon from '../assets/integrations/icalendar-off.png'
import CalendarOnIcon from '../assets/integrations/icalendar-on.png'
import CalendarSyncIcon from '../assets/integrations/icalendar-sync.png'
import ludzik from '../assets/icons/ludzik.png'
import { mappingIcons } from '../components/Template/Icons/RoomIcon/RoomIcon'
import { IRoom } from '../models/Room'
import { theme } from '../theme'
import { IDimensions } from '../utils/Canvas'
import { colorToRGBA } from '../utils/ColorTransformator/aHexTransformer'
import drawRoomTriangle from './drawRoomTriangle'
import { imageCacheService } from './images/imageCache'
import wrapRoomName from './text/wrapRoomName'
import { IntegrationSync } from '../reducers/airbnb'

const calcRoomSize = defaultMemoize((size, add) =>
  add === '0' || add === null ? `${size}` : `${size}+${add}`,
)

export default (
  ctx: CanvasRenderingContext2D,
  constants: IDimensions,
  y: number,
  room: IRoom,
  sync: IntegrationSync,
) => {
  const { imageCache, roomImageCache } = imageCacheService
  // Draw divider line
  ctx.fillStyle = theme.colors.calendar.gridBorder
  ctx.fillRect(0, y, constants.ROOM_WIDTH, constants.GRID_BORDER_WIDTH)

  const iconsY =
    y +
    constants.GRID_HEIGHT -
    constants.ROOM_INTEGRATION_ICON_SIZE -
    constants.ROOM_PADDING_BOTTOM
  const drawRoomType = img =>
    ctx.drawImage(
      img,
      constants.ROOM_PADDING_LEFT,
      iconsY,
      constants.ROOM_INTEGRATION_ICON_SIZE,
      constants.ROOM_INTEGRATION_ICON_SIZE,
    )
  const mappingIcon = mappingIcons.get(room.type)
  if (mappingIcon) {
    roomImageCache(mappingIcon)(drawRoomType)
  }
  if (
    !room.customColor ||
    (room.customColor.hex() === '#000000' && room.customColor.alpha() === 0)
  ) {
    // No custom color, default
    ctx.fillStyle = `rgba(255, 255, 255, 0.2)`
  } else {
    if (room.customColor.hex() !== '#FFFFFF') {
      drawRoomTriangle(
        ctx,
        constants.ROOM_WIDTH,
        y + constants.GRID_HEIGHT,
        constants.ROOM_TRIANGLE_SIZE,
        constants.ROOM_TRIANGLE_SIZE,
        room.customColor.rgb().toString(),
      )
    }
    ctx.fillStyle = colorToRGBA(room.customColor, 0.2)
  }
  // We always draw rect
  ctx.fillRect(
    0,
    y,
    constants.ROOM_WIDTH,
    constants.GRID_HEIGHT + constants.GRID_BORDER_WIDTH,
  )
  if ('calendarDrawRoom' in window) {
    ;(window as any).calendarDrawRoom(
      {
        x: 0,
        y: y,
        width: constants.ROOM_WIDTH,
        height: constants.GRID_HEIGHT + constants.GRID_BORDER_WIDTH,
      },
      room,
      sync,
    )
  }

  ctx.fillStyle = 'rgb(0,0,0)'
  ctx.textBaseline = 'top'
  ctx.font = `500 ${constants.ROOM_FONT_SIZE}px Montserrat`
  const lines = wrapRoomName(
    ctx,
    room.name,
    constants.ROOM_WIDTH -
      constants.ROOM_PADDING_RIGHT -
      (room.iCalendarSync
        ? constants.ROOM_INTEGRATION_ICON_SIZE + constants.ROOM_PADDING_RIGHT
        : constants.ROOM_WRAP_FROM_RIGHT),
    2,
  )
  lines.forEach((line, idx) => {
    ctx.fillText(
      `${line}`,
      constants.ROOM_PADDING_LEFT,
      y +
        (lines.length > 1
          ? constants.ROOM_PADDING_TOP + constants.ROOM_SINGLE_LINE_PADDING_TOP
          : constants.ROOM_PADDING_TOP) +
        constants.ROOM_LINE_HEIGHT * idx,
    )
  })

  const drawIntegration = idx => img => {
    ctx.drawImage(
      img,
      constants.ROOM_WIDTH -
        constants.ROOM_INTEGRATION_ICON_SIZE -
        idx * constants.ROOM_INTEGRATION_ICON_SIZE -
        constants.ROOM_PADDING_RIGHT +
        +!!idx * constants.ROOM_INTEGRATION_ICON_OVERLAP,
      iconsY,
      constants.ROOM_INTEGRATION_ICON_SIZE,
      constants.ROOM_INTEGRATION_ICON_SIZE,
    )
  }

  imageCache(room.iCalendar ? CalendarOnIcon : CalendarOffIcon)(
    drawIntegration(0),
  )

  imageCache(room.booking ? BookingOnIcon : BookingOffIcon)(drawIntegration(1))
  imageCache(
    room.airbnb_mapping && sync === IntegrationSync.Syncing
      ? AirnbnbOn
      : AirnbnbOff,
  )(drawIntegration(2))

  const drawPersonIcon = img =>
    ctx.drawImage(
      img,
      constants.ROOM_PADDING_LEFT + constants.ROOM_TYPE_ICON_SIZE + 4,
      y +
        constants.GRID_HEIGHT -
        constants.ROOM_TYPE_ICON_SIZE -
        constants.ROOM_PADDING_BOTTOM,
      constants.ROOM_TYPE_ICON_SIZE,
      constants.ROOM_TYPE_ICON_SIZE,
    )
  imageCache(ludzik)(drawPersonIcon)
  ctx.font = `500 ${constants.ROOM_SIZE_FONT_SIZE}px Montserrat`
  ctx.fillText(
    calcRoomSize(room.roomSize, room.additionalBeds),
    constants.ROOM_PADDING_LEFT +
      constants.ROOM_INTEGRATION_ICON_SIZE * 2 +
      constants.ROOM_SIZE_TEXT_OFFSET,
    iconsY,
  )

  if (room.iCalendarSync !== 0) {
    const syncIcon =
      room.iCalendarSync === 3
        ? CalendarSyncIcon
        : room.iCalendarSync === 1
        ? CalendarImportIcon
        : CalendarExportIcon

    const drawIntegrationIcon = img =>
      ctx.drawImage(
        img,
        constants.ROOM_WIDTH -
          constants.ROOM_INTEGRATION_ICON_SIZE -
          constants.ROOM_PADDING_RIGHT,
        iconsY - constants.ROOM_INTEGRATION_ICON_SIZE,
        constants.ROOM_INTEGRATION_ICON_SIZE,
        constants.ROOM_INTEGRATION_ICON_SIZE,
      )
    imageCache(syncIcon)(drawIntegrationIcon)
  }
}
